/* tslint:disable: jsx-no-lambda */
import * as React from 'react';
import {
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Button,
  CircularProgress,
  Link,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

import { getBulkQuotationStatus } from 'services/BulkQuotationService';
import { POLL_INTERVAL_MS, POLL_MAX_MS } from 'pages/BulkQuotation/resources';
import { useStyles } from './styles';

interface BatchResultsProps {
  batchId: string;
  onNewBatch: () => void;
}

const LOTE_STATUS_COLOR: Record<string, string> = {
  PROCESSING: 'default',
  COMPLETED: 'primary',
  PARTIAL: 'secondary',
  TIMEOUT: 'secondary',
};

const INSURER_LABEL: Record<string, string> = {
  STORED: 'En proceso',
  SUCCESS: 'Con oferta',
  DATA_ERROR: 'Error de datos',
  ERROR: 'Error técnico',
  TIMEOUT: 'Sin respuesta',
};

const fullName = (q: IBulkQuoteStatusQuote) =>
  [q.firstName, q.lastName].filter(Boolean).join(' ').trim() || '—';

const vehicleLabel = (q: IBulkQuoteStatusQuote) =>
  [q.brand, q.modelo].filter(Boolean).join(' ').trim() || '—';

const QuoteRow: React.FC<{ quote: IBulkQuoteStatusQuote }> = ({ quote }) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const insurerKeys = Object.keys(quote.insurers || {});
  const ciudad =
    quote.cityCirculation && quote.cityCirculation.length ? quote.cityCirculation.join(', ') : '—';

  return (
    <>
      <TableRow className={classes.quoteRow}>
        <TableCell className={classes.narrow}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{quote.consecutiveTotalNumber || '—'}</TableCell>
        <TableCell className={classes.plate}>{quote.licenseVehicle || '—'}</TableCell>
        <TableCell>{fullName(quote)}</TableCell>
        <TableCell className={classes.muted}>{vehicleLabel(quote)}</TableCell>
        <TableCell className={classes.muted}>{ciudad}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={quote.state === 'complete' ? 'Terminada' : 'En proceso'}
            color={quote.state === 'complete' ? 'primary' : 'default'}
          />
        </TableCell>
        <TableCell>
          {quote.outcome === 'priced'
            ? 'Con oferta'
            : quote.outcome === 'no_offers'
            ? 'Sin oferta'
            : '—'}
        </TableCell>
        <TableCell>
          <Link href={`/app/cotizador/autos/resumen/${quote.uuid}`} target="_blank" rel="noopener">
            Ver detalle
          </Link>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={classes.collapseCell} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className={classes.detail}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Aseguradora</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Planes / mensaje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {insurerKeys.map((k) => {
                    const ins = quote.insurers[k];
                    return (
                      <TableRow key={k}>
                        <TableCell>{k}</TableCell>
                        <TableCell>{INSURER_LABEL[ins.state] || ins.state}</TableCell>
                        <TableCell>
                          {ins.state === 'SUCCESS' && ins.quotations && ins.quotations.length ? (
                            <ul className={classes.plans}>
                              {ins.quotations.map((p, idx) => (
                                <li key={idx}>
                                  {p.title}: <strong>${p.value}</strong>
                                  {p.nCotizacion ? ` (${p.nCotizacion})` : ''}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            ins.message || '—'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const BatchResults: React.FC<BatchResultsProps> = ({ batchId, onNewBatch }) => {
  const classes = useStyles();
  const [data, setData] = React.useState<IBulkQuoteStatusResponse | null>(null);
  const [error, setError] = React.useState<string>('');
  const [stoppedByClock, setStoppedByClock] = React.useState(false);
  const [lastUpdate, setLastUpdate] = React.useState<Date | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const res = await getBulkQuotationStatus(batchId);
        if (cancelled) return;
        setData(res);
        setLastUpdate(new Date());
        setError('');

        if (res.status !== 'PROCESSING') return; // terminal → stop
        if (Date.now() - startedAt > POLL_MAX_MS) {
          setStoppedByClock(true);
          return;
        }
        timer = window.setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError('No se pudo consultar el estado del lote. Reintentando...');
        timer = window.setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [batchId]);

  const isProcessing = !data || data.status === 'PROCESSING';

  return (
    <div>
      <div className={classes.header}>
        <div>
          <Typography variant="h6">Seguimiento del lote</Typography>
          <Typography variant="caption" className={classes.mono}>
            {batchId}
          </Typography>
        </div>
        <div className={classes.headerRight}>
          {data && (
            <Chip
              label={data.status}
              color={(LOTE_STATUS_COLOR[data.status] as any) || 'default'}
            />
          )}
          {isProcessing && !stoppedByClock && (
            <CircularProgress size={20} className={classes.spinner} />
          )}
          <Button size="small" onClick={onNewBatch}>
            Nuevo lote
          </Button>
        </div>
      </div>

      {error && (
        <Typography variant="body2" className={classes.error}>
          {error}
        </Typography>
      )}
      {stoppedByClock && (
        <Typography variant="body2" className={classes.warn}>
          El lote sigue en proceso tras 25 minutos. Se detuvo la actualización automática; podés
          volver a abrir esta pantalla más tarde para revisar.
        </Typography>
      )}
      {data && (
        <Typography variant="body2" className={classes.summary}>
          {data.summary.complete}/{data.summary.quotes} terminadas · {data.summary.priced} con
          oferta · {data.summary.pending} pendientes
          {lastUpdate ? ` · actualizado ${lastUpdate.toLocaleTimeString()}` : ''}
        </Typography>
      )}

      {!data && !error && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}

      {data && (
        <div className={classes.tableScroll}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow className={classes.headRow}>
                <TableCell className={classes.narrow} />
                <TableCell>N° cotización</TableCell>
                <TableCell>Placa</TableCell>
                <TableCell>Tomador</TableCell>
                <TableCell>Vehículo</TableCell>
                <TableCell>Ciudad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Resultado</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.quotes.map((q) => (
                <QuoteRow key={q.uuid} quote={q} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default BatchResults;
